import axios from 'axios';

/**
 * API client for the CSV import system
 */
class CsvImportApi {
  constructor() {
    // Use the absolute URL with port 8000 for development
    const baseURL = process.env.NODE_ENV === 'development'
      ? 'http://localhost:8000/api/csv-import'
      : '/api/csv-import';

    // Get the authentication token from localStorage
    const token = localStorage.getItem('token');

    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Token ${token}` : '',
      },
      withCredentials: true,
    });

    // Add response interceptor for better error handling
    this.client.interceptors.response.use(
      response => response,
      error => {
        console.error('API request failed:', error);
        return Promise.reject(error);
      }
    );
  }

  /**
   * Handle API errors
   * @param {Error} error - The error object
   * @throws {Error} - Throws an error with a formatted message
   */
  handleError(error) {
    console.error('API error details:', error);
    const message = error.response?.data?.error ||
                    error.response?.data?.detail ||
                    error.message ||
                    'An error occurred';
    throw new Error(message);
  }

  /**
   * Upload a CSV file
   * @param {FormData} formData - Form data containing the CSV file and entity type
   * @returns {Promise<Object>} - Import session data
   */
  async uploadCsvFile(formData) {
    try {
      // Use the full URL for the upload endpoint to avoid path issues
      const uploadUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:8000/api/csv-import/upload-csv/'
        : '/api/csv-import/upload-csv/';

      console.log('API client uploading to:', uploadUrl);

      // Get the authentication token from localStorage
      const token = localStorage.getItem('token');

      const response = await axios.post(uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Token ${token}` : '',
        },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error('API client upload error:', error);
      this.handleError(error);
    }
  }

  /**
   * Get information about the uploaded CSV file
   * @param {string} sessionId - Import session ID
   * @returns {Promise<Object>} - CSV file information
   */
  async getCsvInfo(sessionId) {
    try {
      console.log(`Getting CSV info for session: ${sessionId}`);
      const response = await this.client.get(`/${sessionId}/get-csv-info/`);
      return response.data;
    } catch (error) {
      console.error(`Error getting CSV info for session ${sessionId}:`, error);
      this.handleError(error);
    }
  }

  /**
   * Get suggested field mappings
   * @param {string} sessionId - Import session ID
   * @returns {Promise<Object>} - Suggested field mappings
   */
  async suggestFieldMappings(sessionId) {
    try {
      console.log(`Getting suggested field mappings for session: ${sessionId}`);
      const response = await this.client.get(`/${sessionId}/suggest-field-mappings/`);
      return response.data;
    } catch (error) {
      console.error(`Error getting suggested field mappings for session ${sessionId}:`, error);
      this.handleError(error);
    }
  }

  /**
   * Update field mappings
   * @param {string} sessionId - Import session ID
   * @param {Object} mappings - Field mappings
   * @returns {Promise<Object>} - Updated field mappings
   */
  async updateFieldMappings(sessionId, mappings) {
    try {
      const response = await this.client.post(`/${sessionId}/update-field-mappings/`, {
        mappings,
      });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Set default values for unmapped required fields
   * @param {string} sessionId - Import session ID
   * @param {Object} defaultValues - Default values
   * @returns {Promise<Object>} - Updated field mappings
   */
  async setDefaultValues(sessionId, defaultValues) {
    try {
      const response = await this.client.post(`/${sessionId}/set-default-values/`, {
        default_values: defaultValues,
      });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Validate CSV data
   * @param {string} sessionId - Import session ID
   * @returns {Promise<Object>} - Validation results
   */
  async validateCsvData(sessionId) {
    try {
      const response = await this.client.post(`/${sessionId}/validate-csv-data/`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get validation results
   * @param {string} sessionId - Import session ID
   * @returns {Promise<Object>} - Validation results
   */
  async getValidationResults(sessionId) {
    try {
      const response = await this.client.get(`/${sessionId}/get-validation-results/`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get validation progress
   * @param {string} sessionId - Import session ID
   * @returns {Promise<Object>} - Validation progress
   */
  async getValidationProgress(sessionId) {
    try {
      const response = await this.client.get(`/${sessionId}/get-validation-progress/`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Correct validation errors
   * @param {string} sessionId - Import session ID
   * @param {Object} corrections - Error corrections
   * @returns {Promise<Object>} - Updated validation results
   */
  async correctValidationErrors(sessionId, corrections) {
    try {
      const response = await this.client.post(`/${sessionId}/correct-validation-errors/`, {
        corrections,
      });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get available options for a field
   * @param {string} sessionId - Import session ID
   * @param {string} fieldName - Field name
   * @returns {Promise<Object>} - Field options
   */
  async getFieldOptions(sessionId, fieldName) {
    try {
      const response = await this.client.get(`/${sessionId}/get-field-options/`, {
        params: { field_name: fieldName }
      });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Detect duplicate entries
   * @param {string} sessionId - Import session ID
   * @returns {Promise<Object>} - Duplicate candidates
   */
  async detectDuplicates(sessionId) {
    try {
      const response = await this.client.post(`/${sessionId}/detect-duplicates/`);
      // If the API call was successful but reported no duplicates, return an empty array
      if (response.data && response.data.success === false && response.data.message && response.data.message.includes('No duplicates found')) {
        return []; // Treat as success with no items
      }
      return response.data; // Otherwise, return the data (could be duplicates or an unexpected structure)
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get entity-specific fields for duplicate resolution
   * @param {string} sessionId - Import session ID
   * @returns {Promise<Object>} - Summary and editable fields for the entity type
   */
  async getDuplicateFields(sessionId) {
    try {
      const response = await this.client.get(`/${sessionId}/get-duplicate-fields/`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Resolve duplicate entries
   * @param {string} sessionId - Import session ID
   * @param {Object} resolutions - Duplicate resolutions (key: duplicateId, value: resolution)
   * @returns {Promise<Object>} - Updated duplicate candidates
   */
  async resolveDuplicates(sessionId, resolutions) {
    try {
      const response = await this.client.post(`/${sessionId}/resolve-duplicates/`, {
        resolutions,
      });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get preview of the data to be imported
   * @param {string} sessionId - Import session ID
   * @returns {Promise<Object>} - Preview data
   */
  async getPreviewData(sessionId) {
    try {
      const response = await this.client.get(`/${sessionId}/get-preview-data/`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Import CSV data
   * @param {string} sessionId - Import session ID
   * @returns {Promise<Object>} - Import results
   */
  async importCsvData(sessionId) {
    try {
      const response = await this.client.post(`/${sessionId}/import-csv-data/`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get import progress
   * @param {string} sessionId - Import session ID
   * @returns {Promise<Object>} - Import progress
   */
  async getImportProgress(sessionId) {
    try {
      const response = await this.client.get(`/${sessionId}/get-import-progress/`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Cancel an import
   * @param {string} sessionId - Import session ID
   * @returns {Promise<Object>} - Cancellation result
   */
  async cancelImport(sessionId) {
    try {
      const response = await this.client.post(`/${sessionId}/cancel-import/`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get import summary
   * @param {string} sessionId - Import session ID
   * @returns {Promise<Object>} - Import summary
   */
  async getImportSummary(sessionId) {
    try {
      const response = await this.client.get(`/${sessionId}/get-import-summary/`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Download error report
   * @param {string} sessionId - Import session ID
   * @returns {Promise<boolean>} - Success status
   */
  async downloadErrorReport(sessionId) {
    try {
      const response = await this.client.get(`/${sessionId}/download-error-report/`, {
        responseType: 'blob',
      });

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `import-errors-${sessionId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Download error CSV
   * @param {string} sessionId - Import session ID
   * @param {Object} options - Additional options
   * @returns {Promise<boolean>} - Success status
   */
  async downloadErrorCsv(sessionId, options = {}) {
    try {
      let apiUrl = `/${sessionId}/download-error-csv/`;

      // If we have skipped rows, send them as POST data
      let response;
      if (options.include_skipped_rows && options.skipped_rows) {
        response = await this.client.post(apiUrl,
          { skipped_rows: options.skipped_rows },
          { responseType: 'blob' }
        );
      } else {
        response = await this.client.get(apiUrl, { responseType: 'blob' });
      }

      // Create a download link and trigger it
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `unrecorded-data-${sessionId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      return true;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get system fields for an entity type
   * @param {string} entityType - Entity type
   * @returns {Promise<Array>} - System fields
   */
  async getSystemFields(entityType) {
    try {
      const response = await this.client.get(`/system-fields/?entity_type=${entityType}`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }
}

// Create a singleton instance
const csvImportApi = new CsvImportApi();

export default csvImportApi;
